import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useNavigate, Link } from 'react-router-dom';
import { UserHttpService } from 'core/services/user';
import AuthLayout from 'core/layout/AuthLayout';
import {
    Alert,
    Box,
    Button,
    Card,
    CardContent,
    FormControl,
    FormControlLabel,
    FormLabel,
    Radio,
    RadioGroup,
    Step,
    StepLabel,
    Stepper,
    TextField,
    Typography,
    IconButton,
    InputAdornment,
    Stack
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import BusinessIcon from '@mui/icons-material/Business';
import PersonIcon from '@mui/icons-material/Person';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import HomeIcon from '@mui/icons-material/Home';

const steps = ['Choose Role', 'Create Account'];

function SignupPage() {
    const navigate = useNavigate();
    const [activeStep, setActiveStep] = useState(0);
    const [selectedRole, setSelectedRole] = useState('employee');
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const [successMessage, setSuccessMessage] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    const { register, handleSubmit, formState: { errors }, watch, setValue } = useForm({
        defaultValues: {
            role: 'employee'
        }
    });

    const handleNext = () => {
        setActiveStep((prevActiveStep) => prevActiveStep + 1);
        setErrorMessage("");
    };

    const handleBack = () => {
        setActiveStep((prevActiveStep) => prevActiveStep - 1);
        setErrorMessage("");
    };

    const getResponseError = (response) => {
        if (!response) return 'Unable to create account. Please try again.';
        if (response.detail) return response.detail;
        if (response.email) return Array.isArray(response.email) ? response.email[0] : response.email;
        if (Array.isArray(response) && response[0]) return response[0];
        if (typeof response === 'string') return response;
        return 'An error occurred. Please try again.';
    };

    const onSubmit = async (data) => {
        if (loading) return;

        setLoading(true);
        setErrorMessage("");
        setSuccessMessage("");

        // Prepare registration data
        const registrationData = {
            email: data.email,
            password: data.password,
            first_name: data.first_name,
            last_name: data.last_name,
            role: selectedRole,
            redirect_url: window.location.origin
        };

        try {
            // Use the register endpoint
            const response = await UserHttpService.register(registrationData);
            
            if (response && !response.email && !response.detail) {
                // Success - show message
                setSuccessMessage('Account created successfully! Please check your email to verify your account.');
            } else {
                setErrorMessage(getResponseError(response));
            }
        } catch (error) {
            console.error('Registration error:', error);
            setErrorMessage('Unable to create account. Please check your connection and try again.');
        } finally {
            setLoading(false);
        }
    };

    const renderRoleSelection = () => {
        return (
            <Box sx={{ mt: 3 }}>
                <FormControl component="fieldset">
                    <FormLabel component="legend" sx={{ mb: 2, fontSize: '1.1rem', fontWeight: 500 }}>
                        I want to sign up as:
                    </FormLabel>
                    <RadioGroup
                        value={selectedRole}
                        onChange={(e) => {
                            setSelectedRole(e.target.value);
                            setValue('role', e.target.value);
                        }}
                    >
                        <Card 
                            variant="outlined" 
                            sx={{ 
                                mb: 2, 
                                cursor: 'pointer',
                                border: selectedRole === 'employee' ? '2px solid #4a5bb0' : '1px solid #e0e0e0',
                                '&:hover': { borderColor: '#4a5bb0' }
                            }}
                            onClick={() => {
                                setSelectedRole('employee');
                                setValue('role', 'employee');
                            }}
                        >
                            <CardContent>
                                <FormControlLabel
                                    value="employee"
                                    control={<Radio />}
                                    label={
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <PersonIcon />
                                            <Box>
                                                <Typography variant="h6">Job Seeker</Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    If you are looking for a job or want to use it to build your CV
                                                </Typography>
                                            </Box>
                                        </Box>
                                    }
                                    sx={{ m: 0, width: '100%' }}
                                />
                            </CardContent>
                        </Card>

                        <Card 
                            variant="outlined" 
                            sx={{ 
                                mb: 2, 
                                cursor: 'pointer',
                                border: selectedRole === 'employer' ? '2px solid #4a5bb0' : '1px solid #e0e0e0',
                                '&:hover': { borderColor: '#4a5bb0' }
                            }}
                            onClick={() => {
                                setSelectedRole('employer');
                                setValue('role', 'employer');
                            }}
                        >
                            <CardContent>
                                <FormControlLabel
                                    value="employer"
                                    control={<Radio />}
                                    label={
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                            <BusinessIcon />
                                            <Box>
                                                <Typography variant="h6">Hiring Manager</Typography>
                                                <Typography variant="body2" color="text.secondary">
                                                    If you want to post job openings and find qualified candidates for your company
                                                </Typography>
                                            </Box>
                                        </Box>
                                    }
                                    sx={{ m: 0, width: '100%' }}
                                />
                            </CardContent>
                        </Card>
                    </RadioGroup>
                </FormControl>

                <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
                    <Button
                        variant="contained"
                        onClick={handleNext}
                        disabled={!selectedRole}
                    >
                        Next
                    </Button>
                </Box>
            </Box>
        );
    };

    const renderRegistrationForm = () => {
        return (
            <Box component="form" onSubmit={handleSubmit(onSubmit)} sx={{ mt: 3 }}>
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
                    <TextField
                        fullWidth
                        label="First Name"
                        {...register("first_name", { required: "First name is required" })}
                        error={Boolean(errors.first_name)}
                        helperText={errors.first_name?.message}
                    />

                    <TextField
                        fullWidth
                        label="Last Name"
                        {...register("last_name", { required: "Last name is required" })}
                        error={Boolean(errors.last_name)}
                        helperText={errors.last_name?.message}
                    />
                </Box>

                <TextField
                    fullWidth
                    label="Email address"
                    type="email"
                    margin="normal"
                    {...register("email", { 
                        required: "Email is required",
                        pattern: {
                            value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                            message: "Invalid email address"
                        }
                    })}
                    error={Boolean(errors.email)}
                    helperText={errors.email?.message}
                />

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2, mt: 2 }}>
                    <TextField
                        fullWidth
                        label="Password"
                        type={showPassword ? 'text' : 'password'}
                        {...register("password", { 
                            required: "Password is required",
                            minLength: {
                                value: 6,
                                message: "Password must be at least 6 characters"
                            }
                        })}
                        error={Boolean(errors.password)}
                        helperText={errors.password?.message}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        edge="end"
                                        aria-label="toggle password visibility"
                                        onClick={() => setShowPassword((prev) => !prev)}
                                    >
                                        {showPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            )
                        }}
                    />

                    <TextField
                        fullWidth
                        label="Confirm Password"
                        type={showConfirmPassword ? 'text' : 'password'}
                        {...register("confirmPassword", { 
                            required: "Please confirm your password",
                            validate: value => value === watch("password") || "Passwords do not match"
                        })}
                        error={Boolean(errors.confirmPassword)}
                        helperText={errors.confirmPassword?.message}
                        InputProps={{
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton
                                        edge="end"
                                        aria-label="toggle password visibility"
                                        onClick={() => setShowConfirmPassword((prev) => !prev)}
                                    >
                                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                    </IconButton>
                                </InputAdornment>
                            )
                        }}
                    />
                </Box>

                {errorMessage && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                        {errorMessage}
                    </Alert>
                )}

                {successMessage && (
                    <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                        <Alert 
                            severity="success" 
                            sx={{ 
                                bgcolor: 'transparent',
                                color: 'success.main',
                                '& .MuiAlert-icon': {
                                    color: 'success.main'
                                }
                            }}
                        >
                            {successMessage}
                        </Alert>
                        <Button
                            component={Link}
                            to="/"
                            startIcon={<HomeIcon />}
                            variant="outlined"
                            sx={{ textTransform: 'none' }}
                        >
                            Go Home
                        </Button>
                    </Box>
                )}

                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 3 }}>
                    <Button onClick={handleBack}>
                        Back
                    </Button>
                    <Button
                        type="submit"
                        variant="contained"
                        disabled={loading}
                    >
                        {loading ? 'Creating Account...' : 'Create Account'}
                    </Button>
                </Box>
            </Box>
        );
    };

    return (
        <AuthLayout wide>
            <Box sx={{ textAlign: 'center', mb: 3 }}>
                <Box component="img" alt="Job Flow Connect" src="/assets/images/jlogo.png" sx={{ height: 34, mb: 2.5 }} />
                <Typography variant="h4" fontWeight={800} gutterBottom>
                    Create your account
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    Join Job Flow Connect today
                </Typography>
            </Box>

            <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
                {steps.map((label) => (
                    <Step key={label}>
                        <StepLabel>{label}</StepLabel>
                    </Step>
                ))}
            </Stepper>

            {activeStep === 0 && renderRoleSelection()}
            {activeStep === 1 && renderRegistrationForm()}
        </AuthLayout>
    );
}

export default SignupPage;
